import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API, apiFetch } from './api';

const SubmissionsContext = createContext();

export function SubmissionsProvider({ userId, children }) {
  const [submissions, setSubmissions] = useState([]);
  const [users, setUsers]             = useState([]);
  const [winner, setWinner]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  const refresh = useCallback(async () => {
    try {
      const submissionsUrl = userId ? `${API}/submissions?userId=${userId}` : `${API}/submissions`;
      const [sRes, wRes, uRes] = await Promise.all([
        apiFetch(submissionsUrl),
        apiFetch(`${API}/winner`),
        apiFetch(`${API}/users`),
      ]);
      const sData = await sRes.json();
      const wData = await wRes.json();
      const uData = await uRes.json();
      if (sData.ok) setSubmissions(sData.submissions);
      if (wData.ok) setWinner(wData.winner);
      if (uData.ok) setUsers(uData.users);
      setError(null);
    } catch (e) {
      setError('Cannot reach the server. Check the Worker URL in api.js.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  async function addSubmission(submission) {
    const res  = await apiFetch(`${API}/submissions`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(submission),
    });
    const data = await res.json();
    if (data.ok) await refresh();
    return data;
  }

  async function updateStatus(id, status, declineReason, photos) {
    const body = { action: status === 'approved' ? 'approve' : 'decline', declineReason };
    if (photos !== undefined) body.photos = photos;
    await apiFetch(`${API}/submissions/${id}`, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(body),
    });
    await refresh();
  }

  async function softDelete(id, userName) {
    await apiFetch(`${API}/submissions/${id}`, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ action: 'softDelete', deletedBy: userName }),
    });
    await refresh();
  }

  async function markWinner(id) {
    await apiFetch(`${API}/winner`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ id }),
    });
    setWinner(id);
  }

  async function pinSubmission(id) {
    await apiFetch(`${API}/submissions/${id}`, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ action: 'pin', pinned: true }),
    });
    await refresh();
  }

  async function unpinSubmission(id) {
    await apiFetch(`${API}/submissions/${id}`, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ action: 'pin', pinned: false }),
    });
    await refresh();
  }

  async function toggleLike(submissionId) {
    const res  = await apiFetch(`${API}/reactions/${submissionId}`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ userId }),
    });
    const data = await res.json();
    if (data.ok) {
      // Optimistic update — avoid full refresh for a simple like
      setSubmissions(prev => prev.map(s =>
        s.id === submissionId
          ? { ...s, likeCount: s.likeCount + (data.liked ? 1 : -1), userLiked: data.liked }
          : s
      ));
    }
    return data;
  }

  async function editSubmission(id, about, caption) {
    const res  = await apiFetch(`${API}/submissions/${id}`, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ action: 'edit', about, caption }),
    });
    const data = await res.json();
    if (data.ok) await refresh();
    return data;
  }

  async function approveUser(id) {
    await apiFetch(`${API}/users/${id}`, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ action: 'approve' }),
    });
    await refresh();
  }

  async function rejectUser(id) {
    await apiFetch(`${API}/users/${id}`, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ action: 'reject' }),
    });
    await refresh();
  }

  return (
    <SubmissionsContext.Provider value={{
      submissions, users, winner, loading, error,
      addSubmission, updateStatus, softDelete,
      markWinner, pinSubmission, unpinSubmission,
      toggleLike, editSubmission,
      approveUser, rejectUser, refresh,
    }}>
      {children}
    </SubmissionsContext.Provider>
  );
}

export function useSubmissions() {
  return useContext(SubmissionsContext);
}
